import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Database, CheckCircle, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function DemoDataSetup() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const populateDemoData = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      console.log("Invoking populate-demo-data function...");
      const { data, error: invokeError } = await supabase.functions.invoke(
        "populate-demo-data",
        {
          body: {},
        }
      );

      if (invokeError) {
        throw invokeError;
      }

      console.log("Demo data population result:", data);
      setResult(data);
      toast.success("Demo data populated successfully!");
    } catch (err: any) {
      console.error("Error populating demo data:", err);
      setError(err.message || "Failed to populate demo data");
      toast.error(err.message || "Failed to populate demo data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-4xl font-bold mb-2">Demo Data Setup</h1>
          <p className="text-muted-foreground">
            Populate the database with realistic demo data for Apex Greens Roofing
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Populate Demo Data
            </CardTitle>
            <CardDescription>
              This will create 25 leads, 10 jobs, 50 tasks, 3 campaigns, 10 proposals, and 5 demo users
              for the Apex Greens Roofing organization.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={populateDemoData}
              disabled={loading}
              size="lg"
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Populating Demo Data...
                </>
              ) : (
                <>
                  <Database className="h-4 w-4 mr-2" />
                  Populate Demo Data
                </>
              )}
            </Button>

            {!result && !error && !loading && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Click the button above to create demo users and data. The login credentials below will only work after this step!
                </AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {result && (
              <Alert>
                <CheckCircle className="h-4 w-4 text-green-500" />
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-semibold">Demo data populated successfully!</p>
                    {result.summary && (
                      <div className="text-sm space-y-1">
                        <p>• Users created: {result.summary.users}</p>
                        <p>• Leads created: {result.summary.leads}</p>
                        <p>• Jobs created: {result.summary.jobs}</p>
                        <p>• Tasks created: {result.summary.tasks}</p>
                        <p>• Campaigns created: {result.summary.campaigns}</p>
                        <p>• Proposals created: {result.summary.proposals}</p>
                        <p>• CRM Audit: {result.summary.audit_triggered ? "✓ Triggered" : "✗ Not triggered"}</p>
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Demo User Credentials</CardTitle>
            <CardDescription>
              ⚠️ These credentials only work AFTER populating the demo data above!
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm font-mono">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-semibold text-foreground">Super Admin:</p>
                <p>Email: admin@apexgreens.demo</p>
                <p>Password: DemoPass123!</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-semibold text-foreground">Sales Rep (Sarah):</p>
                <p>Email: sarah.sales@apexgreens.demo</p>
                <p>Password: DemoPass123!</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-semibold text-foreground">Sales Rep (Mike):</p>
                <p>Email: mike.sales@apexgreens.demo</p>
                <p>Password: DemoPass123!</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-semibold text-foreground">Field Rep (John):</p>
                <p>Email: john.field@apexgreens.demo</p>
                <p>Password: DemoPass123!</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-semibold text-foreground">Field Rep (Lisa):</p>
                <p>Email: lisa.field@apexgreens.demo</p>
                <p>Password: DemoPass123!</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>IMPORTANT:</strong> These login credentials will ONLY work AFTER you click the "Populate Demo Data" button above. The demo users don't exist yet until you populate the data first!
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}
